import { useState } from 'react';
import {
  MoreVertical, Eye, Download, Check, Receipt, Send, Mail, RefreshCw, Pencil, XCircle, Trash2,
  FileWarning,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { BillingInvoiceRow } from './invoiceRow';

/**
 * The lifecycle-aware action menu, shared by the workspace and the job Billing
 * tab. Replaces InvoiceActionsMenu, which branched on the old
 * unpaid/paid/voided status.
 *
 * `partially_paid` is reachable again now that the record-payment form takes a
 * part payment (NEW-237); it kept its row even while nothing in the UI could
 * create one, for legacy and backfilled data.
 *
 * | Lifecycle      | Actions                                                                                 |
 * |----------------|-----------------------------------------------------------------------------------------|
 * | draft          | Send by email · Mark as sent · View/Download PDF · Rebuild* · Notes · Void              |
 * | sent           | Resend · Record payment · Payment history · View/Download PDF · Rebuild* · Notes · Void |
 * | partially_paid | Resend · Record payment · Payment history · View/Download PDF · Notes · Void            |
 * | paid           | Resend · Payment history · View/Download PDF · Notes                                    |
 * | voided         | View/Download PDF · Notes · Delete                                                      |
 *
 * *Rebuild is ONE item whose word depends on the kind: a final offers
 * "Regenerate" (rebuilt from the job's current breakdown), every partial offers
 * "Edit invoice" (re-opened in its creation dialog, where the amount or the line
 * selection can be changed). Both need a handler the job Billing tab passes and
 * the workspace does not, so neither appears in the cross-job list.
 *
 * Send and Resend lead the menu because emailing the invoice is what the user
 * came for; "primary" here means FIRST, not a second trigger beside the one
 * this component renders — the table's actions column is `w-12`, sized for a
 * single icon.
 */

export interface InvoiceRowActionsProps {
  invoice: BillingInvoiceRow;
  /** Opens the record-payment form. The amount is the user's, capped at the balance. */
  onRecordPayment?: () => void | Promise<void>;
  onViewPayments?: () => void | Promise<void>;
  onMarkSent?: () => void | Promise<void>;
  /**
   * Opens SendInvoiceDialog. Both surfaces pass it; without it nothing in the
   * app opens that dialog at all.
   */
  onSend?: () => void | Promise<void>;
  onVoid?: () => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  /** Opens InvoicePdfViewerDialog. Same `hasPdf` gate as the download. */
  onViewPdf?: () => void | Promise<void>;
  onDownloadPdf?: () => void | Promise<void>;
  /**
   * FINALS ONLY. Optional, and the job Billing tab is the only caller that
   * passes it. Regenerating rebuilds a final from the job's current breakdown,
   * which is a job-page action; the workspace deliberately does not offer it.
   * Without this prop RegenerateInvoiceDialog would have no trigger and become
   * dead code.
   */
  onRegenerate?: () => void | Promise<void>;
  /**
   * EVERY PARTIAL — free-form and line-item alike. Opens the creation dialog
   * pre-filled on the invoice, so its amount, description or line selection can
   * be replaced in place under the same invoice number. A line-item partial used
   * to go to `onRegenerate`, which could only re-pull the lines it already had.
   *
   * Optional and job-page-only, for the same reason `onRegenerate` is: it
   * changes what a billing document says, which is a decision made on the job
   * rather than in a cross-job list.
   */
  onEditPartial?: () => void | Promise<void>;
  /** Opens the amend flow for a migrated invoice whose amount was never recorded. */
  onAmendAmount?: () => void | Promise<void>;
  /**
   * Writes the sent date — `YYYY-MM-DD`, or null to clear it.
   *
   * Not a menu item: it belongs to the Sent cell, which is where the date is
   * read and so where it is corrected. It travels with the handlers for the
   * same reason `onAmendAmount` does — the table resolves one handler bundle
   * per row, and a second prop threaded through both list components and both
   * callers would buy nothing.
   */
  onSetSentDate?: (date: string | null) => void | Promise<void>;
  /** Rendered inside the menu — the job tab passes its notes popover here. */
  notesSlot?: React.ReactNode;
}

export function InvoiceRowActions({
  invoice,
  onRecordPayment,
  onViewPayments,
  onMarkSent,
  onSend,
  onVoid,
  onDelete,
  onViewPdf,
  onDownloadPdf,
  onRegenerate,
  onEditPartial,
  onAmendAmount,
  notesSlot,
}: InvoiceRowActionsProps) {
  const [open, setOpen] = useState(false);

  // Close on click, then run. Completion feedback is each action's own toast;
  // closing is the acknowledgement.
  const act = (fn: () => void | Promise<void>) => () => {
    setOpen(false);
    void fn();
  };

  const { lifecycle, kind, origin, hasPdf, amountUnknown } = invoice;
  const canRecordPayment = lifecycle === 'sent' || lifecycle === 'partially_paid';
  // A FINAL is rebuilt from the job's whole scope and has no selection to edit,
  // so it keeps Regenerate. EVERY PARTIAL is edited — including a line-item one,
  // whose old Regenerate (re-pull the same lines' current figures) is now just
  // opening Edit and pressing Save.
  //
  // ONE MENU ITEM, two dialogs behind it. The resolved handler is what makes
  // them mutually exclusive by construction rather than by two conditions that
  // could both be true.
  const rebuildable = kind === 'final';
  const editable = kind === 'partial';
  const onRebuild = rebuildable ? onRegenerate : editable ? onEditPartial : undefined;
  const hasHistory = lifecycle !== 'draft' && lifecycle !== 'voided';
  /**
   * Spec D9. A backfilled QuickBooks invoice has no source document, so there
   * is nothing to render: the email would carry a correctly-letterheaded PDF
   * with no line items and a $0.00 total, addressed to a real accounts-payable
   * department. `hasPdf` is the same gate Download PDF uses, and the send route
   * refuses these too — this hides an action that could only ever fail, or
   * worse, succeed.
   *
   * Voided invoices are excluded because a voided invoice is not a receivable.
   */
  const canSend = origin === 'native' && hasPdf && lifecycle !== 'voided';

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          // 44px on touch, compact once there is a pointer and a dense table.
          className="h-11 w-11 xl:h-8 xl:w-8"
          aria-label="Invoice actions"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* The shared primitive is a Popover with unroled divs. Naming the roles
          here makes this menu keyboard- and screen-reader-legible without
          changing a component every other menu in the app also renders. */}
      <DropdownMenuContent align="end" role="menu" aria-label="Invoice actions">
        {/* FIRST — emailing the invoice is what this menu is opened for. A draft
            has never left the building; anything later is a deliberate resend,
            which the dialog makes the user acknowledge with the previous
            attempt's outcome in front of them. */}
        {canSend && onSend && (
          <DropdownMenuItem role="menuitem" onClick={act(onSend)}>
            <Mail className="mr-2 h-4 w-4" />
            {lifecycle === 'draft' ? 'Send by email' : 'Resend'}
          </DropdownMenuItem>
        )}

        {canRecordPayment && onRecordPayment && (
          <DropdownMenuItem role="menuitem" onClick={act(onRecordPayment)}>
            <Check className="mr-2 h-4 w-4" />
            Record payment
          </DropdownMenuItem>
        )}

        {/* Kept beneath Send by email, not replaced by it: FR-4.7's manual
            fallback is for invoices that left by post or an AP portal, which
            are real.

            DRAFTS ONLY. It previously also appeared on a sent invoice as
            "Update sent date", which did nothing: POST /invoices/:id/mark-sent
            is deliberately idempotent and returns early when sent_at is already
            set, so the click changed no data and still raised a success toast.
            Correcting a wrong send date is a real need — the backfill stamps
            one synthetic date across every migrated invoice — but it needs a
            date input and an endpoint that accepts one, not this. */}
        {lifecycle === 'draft' && onMarkSent && (
          <DropdownMenuItem role="menuitem" onClick={act(onMarkSent)}>
            <Send className="mr-2 h-4 w-4" />
            Mark as sent
          </DropdownMenuItem>
        )}

        {hasHistory && onViewPayments && (
          <DropdownMenuItem role="menuitem" onClick={act(onViewPayments)}>
            <Receipt className="mr-2 h-4 w-4" />
            Payment history
          </DropdownMenuItem>
        )}

        {/* A migrated receivable of unknown size. Offering the amend flow is
            the point — the row is otherwise unactionable. */}
        {amountUnknown && onAmendAmount && (
          <DropdownMenuItem role="menuitem" onClick={act(onAmendAmount)}>
            <FileWarning className="mr-2 h-4 w-4" />
            Record the amount
          </DropdownMenuItem>
        )}

        {/* Both hidden rather than disabled when there is no document: a
            backfilled external row has no cached PDF and no snapshot to
            regenerate one from, and QuickBooks holds that document.

            View leads Download because looking is the cheaper intent and the
            more common one — the viewer offers Download in its own header, so
            nothing is further away than one more click. */}
        {hasPdf && onViewPdf && (
          <DropdownMenuItem role="menuitem" onClick={act(onViewPdf)}>
            <Eye className="mr-2 h-4 w-4" />
            View PDF
          </DropdownMenuItem>
        )}

        {hasPdf && onDownloadPdf && (
          <DropdownMenuItem role="menuitem" onClick={act(onDownloadPdf)}>
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </DropdownMenuItem>
        )}

        {/* Rebuilding is how a wrong invoice is fixed, so a DRAFT qualifies too
            — that is where most mistakes are caught, before anything reaches
            the client. It previously required `sent`, which offered the fix only
            once it was too late to avoid a resend.

            Withheld from paid, part-paid and voided invoices: a settled record
            and an audit trail, and a part-paid invoice is a live debt the client
            has already paid against. The backend draws the same line.

            Which dialog opens — and so which word this item carries — depends
            on the invoice. See `onRebuild`. */}
        {onRebuild && (lifecycle === 'draft' || lifecycle === 'sent') && (
          <DropdownMenuItem role="menuitem" onClick={act(onRebuild)}>
            {kind === 'partial'
              ? <Pencil className="mr-2 h-4 w-4" />
              : <RefreshCw className="mr-2 h-4 w-4" />}
            {kind === 'partial' ? 'Edit invoice' : 'Regenerate'}
          </DropdownMenuItem>
        )}

        {notesSlot}

        {/* Not offered on a paid invoice: voiding one would orphan the payments
            recorded against it. Reversing the payment first is the route, which
            is what the append-only ledger exists for. */}
        {lifecycle !== 'voided' && lifecycle !== 'paid' && onVoid && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem role="menuitem" onClick={act(onVoid)} className="text-red-600 hover:text-red-700">
              <XCircle className="mr-2 h-4 w-4" />
              Void invoice
            </DropdownMenuItem>
          </>
        )}

        {lifecycle === 'voided' && onDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem role="menuitem" onClick={act(onDelete)} className="text-red-600 hover:text-red-700">
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
